import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import Header from './Header/Header';
import AnalysisTabs from './AnalysisTabs/AnalysisTabs';
import MainContent from './MainContent/MainContent';
import DataView from './DataView/DataView';
import MapView from './MapView/MapView';
import AnalysisHeader from './AnalysisHeader/AnalysisHeader';
import MobileNavigation from './MobileNavigation/MobileNavigation';
import Footer from './Footer/Footer';
import CityResolutionModal from './Modals/CityResolutionModal';
import YearSelectionModal from './Modals/YearSelectionModal';
import DefaultLocationModal from './Modals/DefaultLocationModal';
import SavedSearchesModal from './Modals/SavedSearchesModal';
import AlertsModal from './Modals/AlertsModal';
import UsageLimitModal from './Modals/UsageLimitModal';
import { getMapRangeFromChartOption, getChartOptionFromMapRange, getDefaultRange } from '../utils/chartMapSync';
import { getMapRangeFromSortKey, getSortKeyFromMapRange, defaultSort, getAggregateTableSortForUrl } from '../utils/sorting';
import { getStateAbbreviation } from '../constants/locations';
import { getCrimeDisplayName } from '../constants/crimes';
import { checkCityResolution, checkLocationYears, saveSearch, deleteSavedSearch, createAlert, updateAlert, deleteAlert, markNotificationsRead, markAllNotificationsRead, trackSearch } from '../services/api';
import { supabase, getApiBaseUrl } from '../services/api';
import {
  buildKrimeUrl,
  buildExportSourceUrl,
  encodeCountyCityCompareUrl,
  encodeRateTypeForUrl,
  getExportDataSourceDisplayName,
  isLocationTypeAggregateLocation,
  parseKrimePathname,
  urlsMatchExceptTableSort,
} from '../utils/urlRouting';
import { updatePageMeta } from '../utils/pageMeta';
import { isNewUtcMonth } from '../utils/planTier';
import { useAppState } from '../hooks/useAppState';
import '../styles/variables.css'; 
import '../styles/global.css';

const PLAN_LIMITS_FE = { free: 5, lieutenant: 50, captain: 200, chief: null };

// URL-shaped snapshot of the app state at the moment of an action. Field
// names match the query-string parameters emitted by utils/urlRouting.js so
// admin dashboards can reason about metadata with the same vocabulary as the
// URL. Values stay in their internal form (e.g. "FBI_CIUS", not "fbi_cius").
const buildSearchContext = (appState = {}) => {
  const {
    selectedCrime, selectedDateRange, selectedSource,
    activeTab, selectedView, isHistorical,
    compareMode, rateViewMode,
    mapRange, chartOption,
    populationGroup, sortConfig,
    currentPage, itemsPerPage,
  } = appState;
  return {
    crime_type:    selectedCrime || null,
    date_range:    selectedDateRange != null ? String(selectedDateRange) : null,
    data_source:   selectedSource || null,
    analysis_tab:  activeTab || null,
    view:          selectedView || null,
    historical:    Boolean(isHistorical),
    compare_scope: compareMode || null,
    rate_type:     rateViewMode || null,
    table_sort:    sortConfig?.key || null,
    sort_dir:      sortConfig?.direction || null,
    chart:         mapRange || chartOption || null,
    pop_group:     populationGroup || null,
    page:          currentPage ?? null,
    per_page:      itemsPerPage ?? null,
  };
};

const Krime = ({ pendingLocation, onPendingLocationConsumed }) => {
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const initialPathname = useRef(routerLocation.pathname);
  const initialSearch = useRef(routerLocation.search);
  const lastProgrammaticUrl = useRef(null);
  const lastProgrammaticNavigateUrlRef = useRef(null);
  const lastUrlNavigateAtMsRef = useRef(0);
  const isInitialNavigation = useRef(true);
  const aggregateCrimeSourceSortPrevRef = useRef(null);
  const sortSetByRouterRef = useRef(false);
  const popCorrectionRef = useRef(false);
  const popGroupSetByRouterRef = useRef(false);

  const [selectedLocation, setSelectedLocation] = useState({
    info: null,
    loading: false,
    error: null
  });
  const [displayedLocation, setDisplayedLocation] = useState({
    info: null,
    loading: false,
    error: null
  });
  const [appState, dispatch] = useAppState();
  const {
    selectedCrime,
    selectedDateRange,
    selectedSource,
    activeTab,
    selectedView,
    isHistorical,
    compareMode,
    rateViewMode,
    mapRange,
    chartOption,
    populationGroup,
    sortConfig,
    currentPage,
    itemsPerPage,
  } = appState;

  const setSelectedCrime = useCallback(
    (v) => dispatch({ type: 'BATCH_UPDATE', partial: { selectedCrime: v, currentPage: 1 } }),
    [dispatch]
  );
  const setSelectedDateRange = useCallback(
    (v) => dispatch({ type: 'SET_DATE_RANGE', value: v }),
    [dispatch]
  );
  const setSelectedSource = useCallback(
    (v) => dispatch({ type: 'BATCH_UPDATE', partial: { selectedSource: v, currentPage: 1 } }),
    [dispatch]
  );
  const setSelectedView = useCallback(
    (v) => dispatch({ type: 'SET_VIEW', value: v }),
    [dispatch]
  );
  const setIsHistorical = useCallback(
    (v) => dispatch({ type: 'SET_HISTORICAL', value: v }),
    [dispatch]
  );
  const setCompareMode = useCallback(
    (v) => dispatch({ type: 'BATCH_UPDATE', partial: { compareMode: v, currentPage: 1 } }),
    [dispatch]
  );
  const setRateViewMode = useCallback(
    (v) => dispatch({ type: 'SET_RATE_VIEW_MODE', value: v }),
    [dispatch]
  );
  const setPopulationGroup = useCallback(
    (v) => dispatch({ type: 'BATCH_UPDATE', partial: { populationGroup: v, currentPage: 1 } }),
    [dispatch]
  );
  const setCurrentPage = useCallback(
    (v) => dispatch({ type: 'SET_PAGE', value: v }),
    [dispatch]
  );
  const setItemsPerPage = useCallback(
    (v) => dispatch({ type: 'SET_ITEMS_PER_PAGE', value: v }),
    [dispatch]
  );

  const [availableYears, setAvailableYears] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [previousDataView, setPreviousDataView] = useState('table');
  const [isPinned, setIsPinned] = useState('pinned');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1023);
  const [showCityResolutionModal, setShowCityResolutionModal] = useState(false);
  const [cityResolutionData, setCityResolutionData] = useState(null);
  const [showYearSelectionModal, setShowYearSelectionModal] = useState(false);
  const [yearModalLocation, setYearModalLocation] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [needsDefaultLocation, setNeedsDefaultLocation] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSavedSearchesModal, setShowSavedSearchesModal] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertNotifications, setAlertNotifications] = useState([]);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [pendingAlertData, setPendingAlertData] = useState(null);
  const [alertsDefaultTab, setAlertsDefaultTab] = useState('notifications');
  const [userPlan, setUserPlan] = useState('free');
  const [searchesUsed, setSearchesUsed] = useState(0);
  const [exportsUsed, setExportsUsed] = useState(0);
  const [printsUsed, setPrintsUsed] = useState(0);
  const [, setSearchLimitReached] = useState(false);
  const [, setVisitedLocations] = useState(new Set());
  const [liveSearchHistory, setLiveSearchHistory] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('searches');
  const [openSubscriptionSignal, setOpenSubscriptionSignal] = useState(0);
  const [closePopupSignal, setClosePopupSignal] = useState(0);
  const [exportContext, setExportContext] = useState(null);
  const exportContextRef = useRef(null);
  const splitViewCaptureRef = useRef(null);

  const handleExportContextChange = useCallback((ctx) => {
    exportContextRef.current = ctx;
    setExportContext(ctx);
  }, []);

  const applySearchStateFromProfile = useCallback((profile) => {
    const plan = profile.subscription_plan || 'free';
    userPlanRef.current = plan;
    setUserPlan(plan);
    const now = new Date();
    const resetDate = profile.search_reset_date ? new Date(profile.search_reset_date) : null;
    const outputMonthRoll = plan !== 'free' && isNewUtcMonth(now, resetDate);

    let history;
    let syncedCount;
    if (plan === 'free') {
      history = Array.isArray(profile.search_history) ? profile.search_history : [];
      syncedCount =
        typeof profile.monthly_searches_used === 'number'
          ? profile.monthly_searches_used
          : history.length;
    } else {
      history =
        !outputMonthRoll && Array.isArray(profile.search_history) ? profile.search_history : [];
      syncedCount = history.length;
    }

    searchesUsedRef.current = syncedCount;
    setSearchesUsed(syncedCount);
    if (profile.default_location_name && profile.default_location_type) {
      defaultLocationRef.current = {
        name: profile.default_location_name,
        type: profile.default_location_type,
      };
    }
    // Keep live output counters in sync with the server's canonical values.
    // Backend shares `search_reset_date` for both searches and outputs, so
    // we reuse the same UTC month check for paid plans. Krime also listens for the
    // 'krime:output-tracked' window event to bump these instantly without
    // waiting for the next profile refresh.
    setExportsUsed(outputMonthRoll ? 0 : (profile.monthly_exports_used || 0));
    setPrintsUsed(outputMonthRoll ? 0 : (profile.monthly_prints_used || 0));
    // Option B: revisits are free within the same calendar month, even across plan changes.
    // The current-month archive entry holds searches from earlier plans in this same month
    // (created by mid-month plan changes in handleSubscriptionActivation). Merge those
    // into the visited set so the frontend's revisit detection agrees with the backend.
    const archive =
      profile.search_history_archive &&
      typeof profile.search_history_archive === 'object' &&
      !Array.isArray(profile.search_history_archive)
        ? profile.search_history_archive
        : {};
    const nowForMonthKey = new Date();
    const currentMonthKey = `${nowForMonthKey.getUTCFullYear()}-${String(nowForMonthKey.getUTCMonth() + 1).padStart(2, '0')}`;
    const currentMonthArchive = Array.isArray(archive[currentMonthKey])
      ? archive[currentMonthKey]
      : [];
    const combinedForVisited = [...history, ...currentMonthArchive];

    const keys = new Set(
      combinedForVisited.map((e) =>
        `${(e.locationName || '').toLowerCase()}::${(e.locationType || '').toLowerCase()}`
      )
    );
    visitedLocationsRef.current = keys;
    setVisitedLocations(keys);
    // Keep liveSearchHistory as just the current-period history for count purposes.
    // The Profile view composes the merged display on its own using the archive.
    setLiveSearchHistory(history);
    const planLimit = PLAN_LIMITS_FE[plan] ?? null;
    if (planLimit !== null && syncedCount >= planLimit) {
      searchLimitReachedRef.current = true;
      setSearchLimitReached(true);
    } else {
      searchLimitReachedRef.current = false;
      setSearchLimitReached(false);
    }
  }, []);

  const refreshSearchStateFromServer = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    const res = await fetch(`${getApiBaseUrl()}/user/profile`, {
      headers: { Authorization: `Bearer ${session.access_token}` }
    });
    if (!res.ok) return;
    applySearchStateFromProfile(await res.json());
  }, [applySearchStateFromProfile]);

  const searchLimitReachedRef = useRef(false);
  const searchesUsedRef = useRef(0);
  const visitedLocationsRef = useRef(new Set());
  const defaultLocationRef = useRef(null);
  const userPlanRef = useRef('free');
  const searchBlockedRef = useRef(false);
  const mapSearchGateRef = useRef({
    getState: () => ({
      atLimit: false,
      visited: new Set(),
      selectedName: '',
      selectedType: '',
    }),
  });

  // Mirror of the reducer state that trackSearch helpers read to build an
  // always-fresh URL context snapshot without having to thread appState
  // through every useCallback dependency array.
  const appStateRef = useRef(appState);
  useEffect(() => { appStateRef.current = appState; }, [appState]);

  // Live output counters. Export/print components dispatch
  // 'krime:output-tracked' after their tracking calls succeed so the
  // Profile > Account tile updates instantly, the same way liveSearchesUsed
  // bumps on every new search. Events with allowed:false are filtered at
  // the source.
  useEffect(() => {
    const onOutputTracked = (e) => {
      const { kind } = e.detail || {};
      if (kind === 'export') setExportsUsed((v) => v + 1);
      else if (kind === 'print') setPrintsUsed((v) => v + 1);
    };
    window.addEventListener('krime:output-tracked', onOutputTracked);
    return () => window.removeEventListener('krime:output-tracked', onOutputTracked);
  }, []);
  const snapshotSearchContext = useCallback(
    () => buildSearchContext(appStateRef.current),
    []
  );

  const isCurrentDefaultLocation = useCallback((name, type) => {
    const def = defaultLocationRef.current;
    if (!def?.name || !def?.type || !name || !type) return false;
    return (
      String(name).toLowerCase() === String(def.name).toLowerCase() &&
      String(type).toLowerCase() === String(def.type).toLowerCase()
    );
  }, []);

  const recordSearchVisit = useCallback(
    (name, type) => {
      if (!name || !type) return false;
      const planLimit = PLAN_LIMITS_FE[userPlanRef.current] ?? null;
      const locationKey = `${String(name).toLowerCase()}::${String(type).toLowerCase()}`;
      const alreadyVisited = visitedLocationsRef.current.has(locationKey);
      const isDefault = isCurrentDefaultLocation(name, type);
      searchBlockedRef.current = false;

      // Current default is always free to open (login auto-load or later select).
      // Setting/changing the default is counted separately via countDefaultChange.
      if (alreadyVisited || isDefault) {
        trackSearch(name, type, snapshotSearchContext()).catch((err) =>
          console.warn('Track revisit failed:', err)
        );
        return true;
      }

      if (
        searchLimitReachedRef.current ||
        (planLimit !== null && searchesUsedRef.current >= planLimit)
      ) {
        searchBlockedRef.current = true;
        setUpgradeReason('searches');
        setShowUpgradeModal(true);
        setClosePopupSignal((prev) => prev + 1);
        return false;
      }
      const newUsed = searchesUsedRef.current + 1;
      searchesUsedRef.current = newUsed;
      visitedLocationsRef.current = new Set([...visitedLocationsRef.current, locationKey]);
      if (planLimit !== null && newUsed >= planLimit) {
        searchLimitReachedRef.current = true;
        setSearchLimitReached(true);
      }
      setSearchesUsed(newUsed);
      setVisitedLocations(new Set(visitedLocationsRef.current));
      const newEntry = {
        locationName: name,
        locationType: type,
        searchedAt: new Date().toISOString(),
        plan_at_search: userPlanRef.current || 'free',
      };
      setLiveSearchHistory((prev) => [...prev, newEntry]);
      trackSearch(name, type, snapshotSearchContext()).catch((err) =>
        console.warn('Track search failed:', err)
      );
      return true;
    },
    [isCurrentDefaultLocation, snapshotSearchContext]
  );

  const reconcileResolvedCitySearchName = useCallback(
    ({ from, to, type: locType }) => {
      if (!from || !to || !locType) return;
      if (from.trim().toLowerCase() === to.trim().toLowerCase()) return;
      const oldKey = `${from.toLowerCase()}::${String(locType).toLowerCase()}`;
      const newKey = `${to.toLowerCase()}::${String(locType).toLowerCase()}`;
      if (!visitedLocationsRef.current.has(oldKey)) return;

      const nextVisited = new Set(visitedLocationsRef.current);
      nextVisited.delete(oldKey);
      nextVisited.add(newKey);
      visitedLocationsRef.current = nextVisited;
      setVisitedLocations(nextVisited);

      setLiveSearchHistory((prev) => {
        let lastIdx = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          const e = prev[i];
          if (
            String(e.locationType || '').toLowerCase() === String(locType).toLowerCase() &&
            String(e.locationName || '').toLowerCase() === from.toLowerCase()
          ) {
            lastIdx = i;
            break;
          }
        }
        if (lastIdx === -1) return prev;
        const next = [...prev];
        next[lastIdx] = { ...next[lastIdx], locationName: to };
        return next;
      });

      const ctx = snapshotSearchContext();
      trackSearch(to, locType, { ...ctx, replaceAliasOf: from }).catch((err) =>
        console.warn('Track search alias replace failed:', err)
      );
    },
    [snapshotSearchContext]
  );

  const yearCheckInProgress = useRef(false);
  const pendingLocationRef = useRef(pendingLocation);
  const onPendingLocationConsumedRef = useRef(onPendingLocationConsumed);

  const buildSelectedLocationInfo = useCallback((location, overrides = {}) => ({
    name: location?.name,
    type: location?.type,
    state: location?.state || null,
    stateAbbr: location?.stateAbbr || null,
    originalSearch: location?.originalSearch || null,
    wasResolved: location?.wasResolved || false,
    fromHeader: true,
    skipResolutionCheck: false,
    skipYearCheck: false,
    clickInitiated: false,
    ...overrides,
  }), []);

  useLayoutEffect(() => {
    const parsed = parseKrimePathname(routerLocation.pathname, routerLocation.search);
    if (parsed.location?.name) {
      updatePageMeta(parsed.location.name, {
        crime: parsed.crime,
        year: parsed.year,
        source: parsed.source,
        tab: parsed.tab,
        view: parsed.view,
        isHistorical: parsed.historical,
        locationData: null,
      });
    }
  }, [routerLocation.pathname, routerLocation.search]);

  useEffect(() => {
    const loadDefaultLocation = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setInitialLoading(false);
          return;
        }
  
        const response = await fetch(`${getApiBaseUrl()}/user/profile`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
  
        if (response.ok) {
          const profile = await response.json();
          if (profile.default_view) {
            dispatch({ type: 'SET_VIEW', value: profile.default_view });
          }
          if (Array.isArray(profile.saved_searches)) {
            setSavedSearches(profile.saved_searches);
          }
          if (Array.isArray(profile.alerts)) {
            setAlerts(profile.alerts);
          }
          if (Array.isArray(profile.alert_notifications)) {
            setAlertNotifications(profile.alert_notifications);
          }
          applySearchStateFromProfile(profile);
          const planLimit = PLAN_LIMITS_FE[userPlanRef.current] ?? null;

          const pending = pendingLocationRef.current;
          const defaultLocation = (profile.default_location_name && profile.default_location_type)
            ? { name: profile.default_location_name, type: profile.default_location_type }
            : null;

          const parsedUrl = parseKrimePathname(initialPathname.current, initialSearch.current);
          const urlLocation = parsedUrl.location;

          if (!defaultLocation && pending) {
            setNeedsDefaultLocation(true);
            setInitialLoading(false);
            return;
          }

          let locationToLoad = defaultLocation;
          let shouldTrackSearch = false;
          // Revisits don't increment the counter but still need to be logged
          // to activity_log (was_revisit: true). Fire-and-forget so we don't
          // block the render.
          let shouldTrackRevisit = false;

          if (pending) {
            const pendingKey = `${(pending.name || '').toLowerCase()}::${(pending.type || '').toLowerCase()}`;
            const pendingAlreadyVisited = visitedLocationsRef.current.has(pendingKey);
            const pendingIsDefault = isCurrentDefaultLocation(pending.name, pending.type);
            const limitHit = searchLimitReachedRef.current ||
              (planLimit !== null && searchesUsedRef.current >= planLimit);

            if (!pendingAlreadyVisited && !pendingIsDefault && limitHit) {
              setUpgradeReason('searches');
              setShowUpgradeModal(true);
            } else {
              locationToLoad = pending;
              shouldTrackSearch = !pendingAlreadyVisited && !pendingIsDefault;
              shouldTrackRevisit = pendingAlreadyVisited || pendingIsDefault;
            }
            onPendingLocationConsumedRef.current?.();
          } else if (urlLocation) {
            const urlKey = `${urlLocation.name.toLowerCase()}::${urlLocation.type.toLowerCase()}`;
            const urlAlreadyVisited = visitedLocationsRef.current.has(urlKey);
            const urlIsDefault = isCurrentDefaultLocation(urlLocation.name, urlLocation.type);
            const limitHit = searchLimitReachedRef.current ||
              (planLimit !== null && searchesUsedRef.current >= planLimit);

            if (!urlAlreadyVisited && !urlIsDefault && limitHit) {
              setUpgradeReason('searches');
              setShowUpgradeModal(true);
            } else {
              locationToLoad = urlLocation;
              shouldTrackSearch = !urlAlreadyVisited && !urlIsDefault;
              shouldTrackRevisit = urlAlreadyVisited || urlIsDefault;
              dispatch({
                type: 'URL_CHANGE',
                parsed: parsedUrl,
                locationInfo: urlLocation,
              });
            }
          } else if (defaultLocation && !urlLocation) {
            // Auto-loading the saved default never increments; only log activity.
            shouldTrackRevisit = true;
          }

          if (shouldTrackSearch && locationToLoad) {
            try {
              await trackSearch(locationToLoad.name, locationToLoad.type, snapshotSearchContext());
              await refreshSearchStateFromServer();
            } catch (err) {
              console.warn('Track search failed:', err);
            }
          } else if (shouldTrackRevisit && locationToLoad) {
            trackSearch(locationToLoad.name, locationToLoad.type, snapshotSearchContext()).catch(err =>
              console.warn('Track revisit failed:', err)
            );
          }

          if (!locationToLoad) {
            setNeedsDefaultLocation(true);
            setInitialLoading(false);
            return;
          }

          setSelectedLocation({
            info: buildSelectedLocationInfo(locationToLoad),
            loading: true,
            error: null
          });
          setInitialLoading(false);
        } else {
          const errBody = await response.text().catch(() => '');
          console.error(
            'Profile fetch failed — plan/history will be wrong until this succeeds.',
            response.status,
            response.statusText,
            errBody.slice(0, 500),
            response.status === 404
              ? '404: The API process does not expose GET /api/user/profile. Redeploy the backend with routes/user.js mounted in server.js (see app.use("/api/user", userRoutes)).'
              : 'If 401, use the same Supabase project for REACT_APP_* and backend SUPABASE_* env vars.'
          );
          setInitialLoading(false);
        }
      } catch (err) {
        console.error('Error loading default location:', err);
        setInitialLoading(false);
      }
    };
  
    loadDefaultLocation();
  }, [applySearchStateFromProfile, refreshSearchStateFromServer, dispatch, buildSelectedLocationInfo, snapshotSearchContext, isCurrentDefaultLocation]);

  const handleLocationSelectRef = useRef(null);

  useLayoutEffect(() => {
    const currentUrl = `${routerLocation.pathname}${routerLocation.search}`;
    if (lastProgrammaticUrl.current === currentUrl) {
      lastProgrammaticUrl.current = null;
      if (navigationType !== 'POP') return;
    }
    if (isInitialNavigation.current) return;
    if (navigationType === 'POP') {
      lastProgrammaticUrl.current = null;
      lastProgrammaticNavigateUrlRef.current = null;
    } else if (
      lastProgrammaticUrl.current != null &&
      lastProgrammaticUrl.current !== currentUrl
    ) {
      return;
    }

    popCorrectionRef.current = true;

    const parsed = parseKrimePathname(routerLocation.pathname, routerLocation.search);

    dispatch({
      type: 'URL_CHANGE',
      parsed,
      locationInfo: parsed.location,
    });

    if (
      parsed.view === 'chart' &&
      parsed.location?.name?.startsWith('All ')
    ) {
      dispatch({ type: 'SET_VIEW', value: 'table' });
    }

    popGroupSetByRouterRef.current = Boolean(parsed.popGroup);
    sortSetByRouterRef.current = Boolean(
      isLocationTypeAggregateLocation(parsed.location) &&
      parsed.tableSort &&
      parsed.tab !== 'reports'
    );

    const urlLocation = parsed.location;
    if (urlLocation) {
      const currentName = selectedLocation?.info?.name;
      const currentType = selectedLocation?.info?.type;
      const currentState = selectedLocation?.info?.state || null;
      const currentStateAbbr = selectedLocation?.info?.stateAbbr || null;
      const locationChanged = 
        currentName !== urlLocation.name ||
        currentType !== urlLocation.type ||
        currentState !== (urlLocation.state || null) ||
        currentStateAbbr !== (urlLocation.stateAbbr || null);

      if (locationChanged) {
        setSelectedLocation({
          info: buildSelectedLocationInfo(urlLocation, {
            skipResolutionCheck: true,
            skipYearCheck: true,
          }),
          loading: false,
          error: null,
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.pathname, routerLocation.search, navigationType, dispatch, buildSelectedLocationInfo]);

  useLayoutEffect(() => {
    if (initialLoading) return;
    const name = selectedLocation?.info?.name;
    if (typeof name !== 'string' || !name.startsWith('All ')) {
      aggregateCrimeSourceSortPrevRef.current = null;
      sortSetByRouterRef.current = false;
      return;
    }
    const locationType = selectedLocation.info.type;
    const isCountyOrCity = locationType === 'county' || locationType === 'city';
    const isAllLocationsNational =
      isCountyOrCity &&
      (name === 'All Counties' || name === 'All Cities');
    const effectiveCompareMode = isAllLocationsNational ? 'national' : compareMode;

    const prev = aggregateCrimeSourceSortPrevRef.current;
    const next = {
      crime: selectedCrime,
      source: selectedSource,
      name,
    };
    if (prev === null) {
      aggregateCrimeSourceSortPrevRef.current = next;
      sortSetByRouterRef.current = false;
      return;
    }
    if (
      prev.crime === selectedCrime &&
      prev.source === selectedSource &&
      prev.name === name
    ) {
      sortSetByRouterRef.current = false;
      return;
    }
    aggregateCrimeSourceSortPrevRef.current = next;
    if (sortSetByRouterRef.current) {
      sortSetByRouterRef.current = false;
      return;
    }
    dispatch({
      type: 'SET_SORT',
      value: defaultSort(
        selectedCrime,
        locationType,
        effectiveCompareMode,
        rateViewMode
      )[activeTab],
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialLoading,
    selectedCrime,
    selectedSource,
    selectedLocation?.info?.name,
    selectedLocation?.info?.type,
    dispatch,
  ]);

  useEffect(() => {
    if (!selectedLocation?.info?.name || !selectedLocation?.info?.type) return;

    const locType = selectedLocation.info?.type?.toLowerCase() || '';
    const isCountyOrCity = locType === 'county' || locType === 'city';
    const isMetro = locType === 'metro';
    const urlExtras = {};
    if (isHistorical) urlExtras.historical = true;
    const countyCityPair = isCountyOrCity && activeTab === 'rates'
      ? encodeCountyCityCompareUrl(
          locType,
          rateViewMode.rateType,
          rateViewMode.rateScope
        )
      : null;
    if (countyCityPair) {
      urlExtras.compareScope = countyCityPair.compareScope;
      urlExtras.rateType = countyCityPair.rateType;
    } else if (isMetro && (activeTab === 'rates' || activeTab === 'reports')) {
      urlExtras.compareScope = rateViewMode.rateScope;
      urlExtras.rateType = encodeRateTypeForUrl(
        locType,
        rateViewMode.rateType,
        rateViewMode.rateScope
      );
    } else if (isCountyOrCity) {
      urlExtras.compareScope = compareMode;
    } else if (compareMode && compareMode !== 'state') {
      urlExtras.compareScope = compareMode;
    }

    if (
      isLocationTypeAggregateLocation(selectedLocation.info) &&
      activeTab !== 'reports'
    ) {
      const { tableSort, sortDir } = getAggregateTableSortForUrl({
        selectedCrime,
        activeTab,
        compareMode,
        rateViewMode,
        locationInfo: selectedLocation.info,
        sortConfig,
      });
      if (tableSort) {
        urlExtras.tableSort = tableSort;
        urlExtras.sortDir = sortDir;
      }
    }

    if ((selectedView === 'chart' || selectedView === 'map') && mapRange) {
      urlExtras.chart = mapRange;
    }

    if (currentPage && currentPage > 1) {
      urlExtras.page = currentPage;
    }
    if (itemsPerPage && itemsPerPage !== 20) {
      urlExtras.perPage = itemsPerPage;
    }

    if (populationGroup && populationGroup !== 'all') {
      urlExtras.popGroup = populationGroup;
    }

    const newUrl = buildKrimeUrl(selectedLocation.info, {
      crime: selectedCrime,
      year: selectedDateRange,
      source: selectedSource,
      tab: activeTab,
      view: selectedView,
      ...urlExtras,
    });
    const currentUrl = `${routerLocation.pathname}${routerLocation.search}`;

    updatePageMeta(selectedLocation.info.name, {
      crime: selectedCrime,
      year: selectedDateRange,
      source: selectedSource,
      tab: activeTab,
      view: selectedView,
      isHistorical,
      locationData: selectedLocation,
      dataViewMode: exportContext?.view || 'locationName',
    });

    if (isInitialNavigation.current) {
      isInitialNavigation.current = false;
      if (newUrl !== '/' && newUrl !== currentUrl) {
        lastProgrammaticUrl.current = newUrl;
        lastProgrammaticNavigateUrlRef.current = newUrl;
        lastUrlNavigateAtMsRef.current = Date.now();
        navigate(newUrl, { replace: true });
      }
      return;
    }

    if (popCorrectionRef.current) {
      popCorrectionRef.current = false;
      lastProgrammaticNavigateUrlRef.current = currentUrl;
      return;
    }

    if (newUrl === '/' || newUrl === currentUrl) {
      popCorrectionRef.current = false;
      if (newUrl !== '/' && newUrl === currentUrl) {
        lastProgrammaticNavigateUrlRef.current = newUrl;
      }
      return;
    }

    if (lastProgrammaticNavigateUrlRef.current === newUrl) {
      return;
    }

    const now = Date.now();
    const sortOnlyDelta =
      lastProgrammaticNavigateUrlRef.current != null &&
      urlsMatchExceptTableSort(lastProgrammaticNavigateUrlRef.current, newUrl);
    const rapidSortRefinement =
      sortOnlyDelta && now - lastUrlNavigateAtMsRef.current < 120;

    const replaceNavigation = popCorrectionRef.current || rapidSortRefinement;

    lastProgrammaticUrl.current = newUrl;
    lastProgrammaticNavigateUrlRef.current = newUrl;
    lastUrlNavigateAtMsRef.current = now;
    navigate(newUrl, { replace: replaceNavigation });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    routerLocation.pathname,
    routerLocation.search,
    selectedLocation?.info?.name,
    selectedLocation?.info?.type,
    selectedCrime,
    selectedDateRange,
    selectedSource,
    activeTab,
    selectedView,
    isHistorical,
    compareMode,
    rateViewMode.rateType,
    rateViewMode.rateScope,
    exportContext?.view,
    sortConfig.key,
    sortConfig.direction,
    mapRange,
    populationGroup,
    currentPage,
    itemsPerPage,
  ]);

  const stateAbbr = React.useMemo(() => {
    const locationType = selectedLocation?.info?.type;
    const isCountyOrCity = locationType === 'county' || locationType === 'city';
    
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [selectedLocation]);

  const mostRecentYear = React.useMemo(() => {
    if (availableYears.length > 0) {
      return Math.max(...availableYears.map(Number)).toString();
    }
    return '2024';
  }, [availableYears]);
  
  const getActiveCompareMode = useCallback(() => {
    const locationType = selectedLocation?.info?.type;
    if (!locationType || (locationType !== 'county' && locationType !== 'city')) return null;
    
    if (activeTab === 'rates') {
      return rateViewMode.rateScope;
    }
    
    return compareMode;
  }, [selectedLocation?.info?.type, activeTab, compareMode, rateViewMode.rateScope]);

  const handleLocationSelect = useCallback(async (selection) => {
    if (!selection?.name || !selection?.type) {
      return;
    }

    if (!selection.info?.isRecursiveCall) {
      const deferForCityResolution =
        selection.type === 'city' &&
        selection.name.includes(',') &&
        !selection.info?.skipResolutionCheck &&
        !selection.info?.skipYearCheck;

      if (!deferForCityResolution) {
        if (!recordSearchVisit(selection.name, selection.type)) {
          return;
        }
      }
    }

    setHoveredLocation(null);

    if (selection.info?.isResolved || selection.info?.skipResolutionCheck) {
      // Continue with normal flow...
    } else if (selection?.type === 'city' && selection.name.includes(',') && !selection.info?.skipResolutionCheck && !selection.info?.skipYearCheck) {
      const parts = selection.name.split(',').map(part => part.trim());
      const cityName = parts[0];
      const stateName = parts[1];
      
      try {
        const resolutionData = await checkCityResolution(cityName, stateName, selectedDateRange, selectedSource);
        
        if (resolutionData) {
          setCityResolutionData(resolutionData);
          setShowCityResolutionModal(true);
          return;
        }

        if (!selection.info?.isRecursiveCall) {
          if (!recordSearchVisit(selection.name, selection.type)) {
            return;
          }
        }

        const availableYearsResponse = await checkLocationYears(
          selection.name, 
          selection.type, 
          selectedSource
        );
        
        const yearsData = Array.isArray(availableYearsResponse) 
          ? { availableYears: availableYearsResponse, constituentYears: null }
          : availableYearsResponse;
        
        if (yearsData.availableYears && yearsData.availableYears.length > 0) {
          const yearsAsStrings = yearsData.availableYears.map(y => String(y));
          const currentYearString = String(selectedDateRange);
          
          const checkYears = yearsData.constituentYears && yearsData.constituentYears.length > 0
            ? yearsData.constituentYears
            : yearsData.availableYears;
          
          const constituentYearsAsStrings = checkYears.map(y => String(y));
          const hasCurrentYear = constituentYearsAsStrings.includes(currentYearString);
          
          setAvailableYears(yearsAsStrings);
          
          if (!hasCurrentYear) {
            const mostRecent = Math.max(...constituentYearsAsStrings.map(y => parseInt(y))).toString();
            
            dispatch({ type: 'SET_DATE_RANGE', value: mostRecent });
            
            const updatedSelection = {
              ...selection,
              info: {
                ...selection.info,
                skipYearCheck: true,
                skipResolutionCheck: true,
                wasConstituentAutoSwitch: true,
                isRecursiveCall: true
              }
            };
            
            handleLocationSelect(updatedSelection);
            return;
          }
          
          if (yearsData.constituentYears && yearsData.constituentYears.length > 0) {
            const mostRecentConstituent = Math.max(...constituentYearsAsStrings.map(y => parseInt(y)));
            const currentYearInt = parseInt(currentYearString);
            
            if (mostRecentConstituent > currentYearInt) {
              setYearModalLocation(selection);
              setShowYearSelectionModal(true);
              return;
            }
          }
        }
      } catch (error) {
        console.error("ERROR checking city years:", error);
      }
    }
    
    if (selection.info?.fromHeader && !selection.info?.skipYearCheck && !selection.info?.wasConstituentAutoSwitch) {
      const wasJustResolved = selection.info?.originalSearch && 
                              selection.info?.originalSearch !== selection.name;
      
      if (!wasJustResolved || selection.info?.wasResolved) {
        try {
          const availableYears = await checkLocationYears(
            selection.name, 
            selection.type, 
            selectedSource
          );
          
          if (availableYears && availableYears.length > 0) {
            const yearsData = Array.isArray(availableYears) 
              ? { availableYears: availableYears, constituentYears: null }
              : availableYears;
            
            const yearsAsStrings = yearsData.availableYears.map(y => String(y));
            const mostRecent = Math.max(...yearsAsStrings.map(y => parseInt(y))).toString();
            const currentYearString = String(selectedDateRange);
            const hasCurrentYear = yearsAsStrings.includes(currentYearString);
            
            setAvailableYears(yearsAsStrings);
            
            if (!hasCurrentYear) {
              dispatch({ type: 'SET_DATE_RANGE', value: mostRecent });
              setAvailableYears(yearsAsStrings);
              
              const updatedSelection = {
                ...selection,
                info: {
                  ...selection.info,
                  skipYearCheck: true,
                  skipResolutionCheck: true,
                  isRecursiveCall: true
                }
              };
              
              handleLocationSelect(updatedSelection);
              return;
            }
            else if (parseInt(mostRecent) > parseInt(currentYearString)) {
              setYearModalLocation(selection);
              setShowYearSelectionModal(true);
              return;
            }
          }
        } catch (error) {
          console.error("Error checking years:", error);
        }
      }
    }

    const prevType = selectedLocation?.info?.type;
    if (
      prevType !== selection.type &&
      (selection.type === 'city' || selection.type === 'county') &&
      !selection.info?.syncSortFromRouter
    ) {
      dispatch({
        type: 'BATCH_UPDATE',
        partial: {
          rateViewMode: { rateType: selection.type, rateScope: 'state' },
          compareMode: 'state',
        },
      });
    }

    const info = {
      name: selection.name,
      type: selection.type,
      state: selection.info?.state || null,
      stateAbbr,
      originalSearch: selection.info?.originalSearch || null,
      wasResolved: selection.info?.wasResolved || false,
      skipResolutionCheck: selection.info?.skipResolutionCheck || false,
      fromHeader: selection.info?.fromHeader || false,
      clickInitiated: selection.info?.clickInitiated ?? false,
    };

    const fromRouterUrl = selection.info?.syncSortFromRouter === true;
    setSelectedLocation({
      info: info,
      loading: fromRouterUrl ? false : true,
      error: null
    });

    if (!fromRouterUrl && isHistorical) {
      dispatch({ type: 'SET_HISTORICAL', value: false });
    }

    const tabForLocationSelect = selection.info?.routerTab ?? activeTab;

    const prevName = selectedLocation?.info?.name ?? '';
    const fromNationalAllCities =
      selection.type === 'city' && prevName === 'All Cities';
    const fromNationalAllCounties =
      selection.type === 'county' && prevName === 'All Counties';

    if (
      (fromNationalAllCities || fromNationalAllCounties) &&
      !selection.info?.syncSortFromRouter
    ) {
      const toStateScopedList =
        selection.name?.startsWith('All ') &&
        selection.name !== 'All Cities' &&
        selection.name !== 'All Counties' &&
        (selection.type === 'city'
          ? selection.name.endsWith(' Cities')
          : selection.name.endsWith(' Counties'));
      const toSpecificJurisdiction = selection.name?.includes(',');
      if (toStateScopedList || toSpecificJurisdiction) {
        const peer = selection.type === 'county' ? 'county' : 'city';
        const nextRateView = { rateType: peer, rateScope: 'state' };
        dispatch({
          type: 'BATCH_UPDATE',
          partial: {
            compareMode: 'state',
            rateViewMode: nextRateView,
            sortConfig: defaultSort(
              selectedCrime,
              selection.type,
              'state',
              nextRateView
            )[tabForLocationSelect],
          },
        });
      }
    }

    if (
      (selection.name === 'All Counties' || selection.name === 'All Cities') &&
      compareMode !== 'national' &&
      !selection.info?.syncSortFromRouter
    ) {
      const newRateViewMode = {
        rateType: selection.type,
        rateScope: 'national',
      };
      dispatch({
        type: 'BATCH_UPDATE',
        partial: {
          compareMode: 'national',
          rateViewMode: newRateViewMode,
          sortConfig: defaultSort(
            selectedCrime,
            selection.type,
            'national',
            newRateViewMode
          )[tabForLocationSelect],
        },
      });
    }
  
    if (selection.info?.syncSortFromRouter) return;

    const isFromSpecificToAll = 
      selectedLocation?.info?.name && !selectedLocation.info.name.startsWith('All ') && 
      selection.name?.startsWith('All ');

    const isGoingToAll = selection.name?.startsWith('All ');
    if (isGoingToAll && selectedView === 'chart') {
      dispatch({ type: 'SET_VIEW', value: 'table' });
    }
    
    if (isFromSpecificToAll) {
      const defaultRange = getDefaultRange(tabForLocationSelect, selection.type);
      dispatch({ type: 'SET_MAP_RANGE', value: defaultRange });

      const isCountyOrCity = selection.type === 'county' || selection.type === 'city';
      const isAllLocationsNational = isCountyOrCity && (
        selection.name === 'All Counties' || 
        selection.name === 'All Cities'
      );
      const compareModeForSort = isAllLocationsNational ? 'national' : 'state';
      
      dispatch({
        type: 'SET_SORT',
        value: defaultSort(
          selectedCrime,
          selection.type,
          compareModeForSort,
          rateViewMode
        )[tabForLocationSelect],
      });

      const newChartOption = getChartOptionFromMapRange(
        defaultRange,
        selection.type.charAt(0).toUpperCase() + selection.type.slice(1),
        selectedCrime,
        tabForLocationSelect,
        getCrimeDisplayName(selectedCrime),
        getActiveCompareMode,
        rateViewMode,
        stateAbbr
      );
      dispatch({ type: 'SET_CHART_OPTION', value: newChartOption });
    } else {
      const newChartOption = getChartOptionFromMapRange(
        mapRange,
        selection.type.charAt(0).toUpperCase() + selection.type.slice(1),
        selectedCrime,
        activeTab,
        getCrimeDisplayName(selectedCrime),
        getActiveCompareMode,
        rateViewMode,
        stateAbbr
      );
      dispatch({ type: 'SET_CHART_OPTION', value: newChartOption });
    }
  }, [
    selectedLocation?.info?.name,
    selectedLocation?.info?.type,
    mapRange,
    activeTab,
    selectedDateRange,
    selectedCrime,
    selectedSource,
    selectedView,
    isHistorical,
    compareMode,
    getActiveCompareMode,
    rateViewMode,
    stateAbbr,
    dispatch,
    recordSearchVisit,
  ]);

  handleLocationSelectRef.current = handleLocationSelect;

  const handleSaveSearch = useCallback(async () => {
    if (!selectedLocation?.info?.name) return false;

    if (userPlan === 'free') {
      setUpgradeReason('saved_searches');
      setShowUpgradeModal(true);
      return false;
    }

    const isDuplicate = savedSearches.some(s =>
      s.locationName === selectedLocation.info.name &&
      s.locationType === selectedLocation.info.type &&
      s.crime === selectedCrime &&
      String(s.dateRange) === String(selectedDateRange) &&
      s.source === selectedSource
    );
    if (isDuplicate) return 'duplicate';

    const newSearch = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      locationName: selectedLocation.info.name,
      locationType: selectedLocation.info.type,
      crime: selectedCrime,
      dateRange: String(selectedDateRange),
      source: selectedSource,
      savedAt: new Date().toISOString()
    };
    try {
      const updated = await saveSearch(newSearch);
      setSavedSearches(updated);
      return 'saved';
    } catch (err) {
      console.error('Error saving search:', err);
      return false;
    }
  }, [selectedLocation?.info, selectedCrime, selectedDateRange, selectedSource, savedSearches, userPlan]);

  const handleDeleteSavedSearch = useCallback(async (index) => {
    try {
      const updated = await deleteSavedSearch(index);
      setSavedSearches(updated);
    } catch (err) {
      console.error('Error deleting saved search:', err);
    }
  }, []);

  const handleApplySavedSearch = useCallback((search) => {
    setAvailableYears([]);
    dispatch({
      type: 'BATCH_UPDATE',
      partial: {
        selectedCrime: search.crime,
        selectedDateRange: search.dateRange,
        selectedSource: search.source,
      },
    });
    handleLocationSelect({
      name: search.locationName,
      type: search.locationType,
      info: { fromHeader: true, skipResolutionCheck: false, skipYearCheck: true }
    });
  }, [handleLocationSelect, dispatch]);

  const handleSetAlert = useCallback(() => {
    if (!selectedLocation?.info?.name) return;

    if (userPlan === 'free') {
      setUpgradeReason('alerts');
      setShowUpgradeModal(true);
      return;
    }

    const existing = alerts.find(
      a => a.locationName === selectedLocation.info.name &&
           a.locationType === selectedLocation.info.type
    );
    if (existing) {
      setPendingAlertData(null);
      setAlertsDefaultTab('manage');
    } else {
      setPendingAlertData({
        locationName: selectedLocation.info.name,
        locationType: selectedLocation.info.type,
        crimeType: selectedCrime
      });
      setAlertsDefaultTab('notifications');
    }
    setShowAlertsModal(true);
  }, [selectedLocation?.info, selectedCrime, alerts, userPlan]);

  const handleSaveAlert = useCallback(async (alertData) => {
    try {
      const updated = await createAlert(alertData);
      setAlerts(updated);
    } catch (err) {
      if (err.message?.includes('403') || err.status === 403) {
        setShowAlertsModal(false);
        setUpgradeReason('alert_limit');
        setShowUpgradeModal(true);
      } else {
        console.error('Error creating alert:', err);
      }
    }
  }, []);

  const handleUpdateAlert = useCallback(async (id, updates) => {
    try {
      const updated = await updateAlert(id, updates);
      setAlerts(updated);
    } catch (err) {
      console.error('Error updating alert:', err);
    }
  }, []);

  const handleDeleteAlert = useCallback(async (id) => {
    try {
      const updated = await deleteAlert(id);
      setAlerts(updated);
    } catch (err) {
      console.error('Error deleting alert:', err);
    }
  }, []);

  const handleMarkNotificationsRead = useCallback(async (ids) => {
    try {
      const updated = await markNotificationsRead(ids);
      setAlertNotifications(updated);
    } catch (err) {
      console.error('Error marking notifications read:', err);
    }
  }, []);

  const handleMarkAllNotificationsRead = useCallback(async () => {
    try {
      const updated = await markAllNotificationsRead();
      setAlertNotifications(updated);
    } catch (err) {
      console.error('Error marking all notifications read:', err);
    }
  }, []);

  const handleDefaultLocationChange = useCallback(async (locationName, locationType) => {
    const planLimit = PLAN_LIMITS_FE[userPlanRef.current] ?? null;
    const locationKey = `${(locationName || '').toLowerCase()}::${(locationType || '').toLowerCase()}`;
    const alreadyVisited = visitedLocationsRef.current.has(locationKey);
    // Required first-time default must stay allowed even when search quota is exhausted
    // (e.g. returning users after account delete).
    const isInitialDefault = !defaultLocationRef.current?.name || !defaultLocationRef.current?.type;

    if (alreadyVisited || isInitialDefault) {
      trackSearch(locationName, locationType, {
        ...snapshotSearchContext(),
        ...(isInitialDefault ? { initialDefault: true } : {}),
      }).catch(err =>
        console.warn(isInitialDefault ? 'Track initial default failed:' : 'Track revisit failed:', err)
      );
      defaultLocationRef.current = { name: locationName, type: locationType };
      return { allowed: true };
    }

    if (searchLimitReachedRef.current || (planLimit !== null && searchesUsedRef.current >= planLimit)) {
      return { allowed: false };
    }
    try {
      // Changing an existing default still consumes a search.
      await trackSearch(locationName, locationType, {
        ...snapshotSearchContext(),
        countDefaultChange: true,
      });
      await refreshSearchStateFromServer();
      defaultLocationRef.current = { name: locationName, type: locationType };
    } catch (err) {
      console.warn('Track search failed:', err);
    }
    return { allowed: true };
  }, [refreshSearchStateFromServer, snapshotSearchContext]);

  useEffect(() => {
    if (!selectedLocation?.info?.clickInitiated) return;
    const t = setTimeout(() => {
      setSelectedLocation((prev) => {
        if (!prev?.info?.clickInitiated) return prev;
        return {
          ...prev,
          info: { ...prev.info, clickInitiated: false },
        };
      });
    }, 0);
    return () => clearTimeout(t);
  }, [selectedLocation?.info?.name, selectedLocation?.info?.type, selectedLocation?.info?.clickInitiated]);

  useEffect(() => {
    if (selectedView === 'table' || selectedView === 'chart') {
      setPreviousDataView(selectedView);
    }
  }, [selectedView]);

  useEffect(() => {
    if (!selectedLocation?.info?.type) {
      setSelectedData(null);
      return;
    }
  
    const locationType = selectedLocation.info.type;
    let defaultSummary = '';
  
    switch (locationType) {
      case 'city':
        defaultSummary = 'Citywide Summary';
        break;
      case 'county':
        defaultSummary = 'Countywide Summary';
        break;
      case 'state':
        defaultSummary = 'Statewide Summary';
        break;
      case 'metro':
        defaultSummary = 'Metrowide Summary';
        break;
      case 'division':
        defaultSummary = 'Division Summary';
        break;
      case 'region':
        defaultSummary = 'Regional Summary';
        break;
      case 'nation':
        defaultSummary = 'National Summary';
        break;
      default:
        defaultSummary = 'Summary Report';
    }
  
    setSelectedData(defaultSummary);
  }, [selectedLocation?.info?.type]);

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 1023;
      const wasDesktop = !isMobile;
      const isNowDesktop = !newIsMobile;
      
      setIsMobile(newIsMobile);
      
      if (wasDesktop === false && isNowDesktop === true) {
        if (selectedView === 'map') {
          dispatch({ type: 'SET_VIEW', value: previousDataView });
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isMobile, selectedView, previousDataView, dispatch]);

  const handleDateRangeChange = (newYear) => {
    if (userPlan === 'free' && newYear !== mostRecentYear) {
      setUpgradeReason('historical_data');
      setShowUpgradeModal(true);
      return;
    }
    if (selectedLocation?.info?.fromHeader) {
      setSelectedLocation(prev => ({
        ...prev,
        info: {
          ...prev.info,
          fromHeader: false
        }
      }));
    }
    dispatch({ type: 'BATCH_UPDATE', partial: { selectedDateRange: newYear, currentPage: 1 } });
  };
  
  const handleChartOptionChange = (newChartOption) => {
    dispatch({
      type: 'BATCH_UPDATE',
      partial: {
        chartOption: newChartOption,
        mapRange: getMapRangeFromChartOption(
          newChartOption,
          activeTab,
          rateViewMode,
          stateAbbr
        ),
      },
    });
  };

  const handleMapRangeChange = (range) => {
    const newChartOption = getChartOptionFromMapRange(
      range,
      selectedLocation?.info?.type?.charAt(0).toUpperCase() +
        selectedLocation?.info?.type?.slice(1),
      selectedCrime,
      activeTab,
      getCrimeDisplayName(selectedCrime),
      getActiveCompareMode,
      rateViewMode,
      stateAbbr
    );

    const newSortKey = getSortKeyFromMapRange(
      range,
      selectedCrime,
      activeTab,
      getActiveCompareMode(),
      rateViewMode,
      selectedLocation?.info?.type === 'county' ||
        selectedLocation?.info?.type === 'city',
      selectedLocation?.info?.type
    );

    if (newSortKey) {
      let direction = 'desc';
      if (newSortKey.includes('rank') || newSortKey === 'name') {
        direction = 'asc';
      }
      dispatch({
        type: 'BATCH_UPDATE',
        partial: {
          mapRange: range,
          chartOption: newChartOption,
          sortConfig: {
            key: newSortKey,
            direction,
          },
          currentPage: 1,
        },
      });
    } else {
      dispatch({
        type: 'BATCH_UPDATE',
        partial: {
          mapRange: range,
          chartOption: newChartOption,
          currentPage: 1,
        },
      });
    }
  };

  const handleSortChange = (newSortConfig) => {
    const newMapRange = getMapRangeFromSortKey(newSortConfig.key);

    if (newMapRange) {
      const newChartOption = getChartOptionFromMapRange(
        newMapRange,
        selectedLocation?.info?.type?.charAt(0).toUpperCase() +
          selectedLocation?.info?.type?.slice(1),
        selectedCrime,
        activeTab,
        getCrimeDisplayName(selectedCrime),
        getActiveCompareMode,
        rateViewMode,
        stateAbbr
      );
      dispatch({
        type: 'BATCH_UPDATE',
        partial: {
          sortConfig: newSortConfig,
          mapRange: newMapRange,
          chartOption: newChartOption,
          currentPage: 1,
        },
      });
    } else {
      dispatch({
        type: 'BATCH_UPDATE',
        partial: {
          sortConfig: newSortConfig,
          currentPage: 1,
        },
      });
    }
  };

  const handleTabChange = (newTab) => {
    popCorrectionRef.current = false;
  
    const locationType = selectedLocation?.info?.type;
    const isCountyOrCity = locationType === 'county' || locationType === 'city';
  
    const batchPartial = {};
  
    if (activeTab === 'rates' && newTab !== 'rates' && isCountyOrCity) {
      batchPartial.compareMode = rateViewMode.rateScope;
      batchPartial.rateViewMode = { ...rateViewMode, rateType: locationType };
    }
  
    let rateViewForSort = rateViewMode;
    if (newTab === 'rates' && activeTab !== 'rates' && isCountyOrCity) {
      rateViewForSort = {
        rateType: locationType,
        rateScope: compareMode,
      };
      batchPartial.rateViewMode = rateViewForSort;
    }
  
    const isAllLocationsNational =
      isCountyOrCity &&
      (selectedLocation?.info?.name === 'All Counties' ||
        selectedLocation?.info?.name === 'All Cities');
    const sortCompareMode = isAllLocationsNational ? 'national' : compareMode;
  
    const sortDefaults = defaultSort(
      selectedCrime,
      selectedLocation?.info?.type,
      sortCompareMode,
      rateViewForSort
    );
  
    const newMapRange = getDefaultRange(newTab, selectedLocation?.info?.type);
    const effectiveRateView = batchPartial.rateViewMode || rateViewMode;
    const effectiveCompare = batchPartial.compareMode || compareMode;
    const tabGetActiveCompareMode = () => {
      if (!locationType || (locationType !== 'county' && locationType !== 'city')) return null;
      if (newTab === 'rates') return effectiveRateView.rateScope;
      return effectiveCompare;
    };
  
    const newChartOption = getChartOptionFromMapRange(
      newMapRange,
      selectedLocation?.info?.type?.charAt(0).toUpperCase() +
        selectedLocation?.info?.type?.slice(1),
      selectedCrime,
      newTab,
      getCrimeDisplayName(selectedCrime),
      tabGetActiveCompareMode,
      effectiveRateView,
      stateAbbr
    );
  
    batchPartial.sortConfig = sortDefaults[newTab];
    batchPartial.activeTab = newTab;
    batchPartial.mapRange = newMapRange;
    batchPartial.chartOption = newChartOption;
    batchPartial.currentPage = 1;
  
    dispatch({ type: 'BATCH_UPDATE', partial: batchPartial });
  };

  const printFooterAttrs = useMemo(() => {
    const ctx = {
      locationData: displayedLocation,
      selectedCrime,
      selectedDateRange,
      selectedSource,
      activeTab,
      selectedView,
      isHistorical,
      compareMode,
      rateViewMode,
    };
    return {
      url: buildExportSourceUrl(ctx),
      dataSource: getExportDataSourceDisplayName(selectedSource),
    };
  }, [
    displayedLocation,
    selectedCrime,
    selectedDateRange,
    selectedSource,
    activeTab,
    selectedView,
    isHistorical,
    compareMode,
    rateViewMode,
  ]);

  if (initialLoading) return null;

  mapSearchGateRef.current.getState = () => {
    const planLimit = PLAN_LIMITS_FE[userPlanRef.current] ?? null;
    const atLimit =
      planLimit !== null &&
      (searchLimitReachedRef.current || searchesUsedRef.current >= planLimit);
    // Treat the saved default as visited for map locking so it stays reachable at limit.
    const visited = new Set(visitedLocationsRef.current);
    const def = defaultLocationRef.current;
    if (def?.name && def?.type) {
      visited.add(`${String(def.name).toLowerCase()}::${String(def.type).toLowerCase()}`);
    }
    return {
      atLimit,
      visited,
      selectedName: selectedLocation?.info?.name ?? '',
      selectedType: selectedLocation?.info?.type ?? '',
    };
  };

  return (
    <div className="main-container">
      {needsDefaultLocation && (
        <DefaultLocationModal
          prefilledLocation={pendingLocation}
          onConsumed={onPendingLocationConsumed}
          onDefaultLocationChange={handleDefaultLocationChange}
          onComplete={(location) => {
            setNeedsDefaultLocation(false);
            defaultLocationRef.current = {
              name: location.name,
              type: location.type,
            };
            setSelectedLocation({
              info: {
                name: location.name,
                type: location.type,
                fromHeader: true,
                skipResolutionCheck: false,
                skipYearCheck: false
              },
              loading: true,
              error: null
            });
          }}
        />
      )}
      <Header 
        isPinned={isPinned}
        setIsPinned={setIsPinned}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        isMobile={isMobile}
        selectedLocation={selectedLocation}
        onLocationSelect={handleLocationSelect}
        selectedCrime={selectedCrime}
        setSelectedCrime={setSelectedCrime}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={handleDateRangeChange}
        selectedSource={selectedSource}
        setSelectedSource={setSelectedSource}
        availableYears={availableYears}
        onDefaultViewChange={setSelectedView}
        onSaveSearch={handleSaveSearch}
        onOpenSavedSearches={() => setShowSavedSearchesModal(true)}
        unreadCount={alertNotifications.filter(n => !n.read).length}
        onOpenAlerts={() => { setPendingAlertData(null); setAlertsDefaultTab('notifications'); setShowAlertsModal(true); }}
        userPlan={userPlan}
        mostRecentYear={mostRecentYear}
        openProfileToSubscription={openSubscriptionSignal}
        liveSearchesUsed={searchesUsed}
        liveExportsUsed={exportsUsed}
        livePrintsUsed={printsUsed}
        liveSearchHistory={liveSearchHistory}
        onDefaultLocationChange={handleDefaultLocationChange}
      />
      <AnalysisTabs 
        activeTab={activeTab} 
        setActiveTab={handleTabChange} 
      />
      <MainContent 
        isPinned={isPinned}
        selectedView={selectedView}
        activeTab={activeTab}
        isMobile={isMobile}
        captureRef={splitViewCaptureRef}
        printFooterUrl={printFooterAttrs.url}
        printDataSourceLabel={printFooterAttrs.dataSource}
        analysisHeader={
          <AnalysisHeader 
            activeTab={activeTab}
            selectedLocation={displayedLocation}
            onLocationSelect={handleLocationSelect}
            hasAlertForCurrentLocation={alerts.some(
              a => a.locationName === displayedLocation?.info?.name &&
                   a.locationType === displayedLocation?.info?.type
            )}
            onSetAlert={handleSetAlert}
          />
        }
        dataView={
          <DataView 
            selectedView={selectedView} 
            setSelectedView={setSelectedView} 
            activeTab={activeTab} 
            setActiveTab={handleTabChange}
            isHistorical={isHistorical}
            setIsHistorical={setIsHistorical}
            isPinned={isPinned}
            setIsPinned={setIsPinned}
            isMobile={isMobile}
            selectedLocation={selectedLocation}
            onLocationSelect={handleLocationSelect}
            displayedLocation={displayedLocation}
            setDisplayedLocation={setDisplayedLocation}
            selectedCrime={selectedCrime}
            setSelectedCrime={setSelectedCrime} 
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={handleDateRangeChange}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
            selectedData={selectedData}
            setSelectedData={setSelectedData}
            compareMode={compareMode}
            onCompareModeChange={setCompareMode}
            rateViewMode={rateViewMode}
            setRateViewMode={setRateViewMode}
            getActiveCompareMode={getActiveCompareMode}
            onChartOptionChange={handleChartOptionChange}
            selectedChartOption={chartOption}
            onMapRangeChange={handleMapRangeChange}
            sortConfig={sortConfig}
            onSortChange={handleSortChange}
            hoveredLocation={hoveredLocation}
            setHoveredLocation={setHoveredLocation}
            setAvailableYears={setAvailableYears}
            availableYears={availableYears}
            showCityResolutionModal={showCityResolutionModal}
            setShowCityResolutionModal={setShowCityResolutionModal}
            setCityResolutionData={setCityResolutionData}
            yearCheckInProgress={yearCheckInProgress}
            onYearChange={setSelectedDateRange}
            setShowYearSelectionModal={setShowYearSelectionModal}
            setYearModalLocation={setYearModalLocation}
            onSaveSearch={handleSaveSearch}
            userPlan={userPlan}
            mostRecentYear={mostRecentYear}
            onRequestUpgrade={(reason) => { setUpgradeReason(reason); setShowUpgradeModal(true); }}
            onExportContextChange={handleExportContextChange}
            captureRef={splitViewCaptureRef}
            populationGroup={populationGroup}
            setPopulationGroup={setPopulationGroup}
            popGroupSetByRouterRef={popGroupSetByRouterRef}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            onResolvedCityDisplayName={reconcileResolvedCitySearchName}
          />
        }
        mapView={
          <MapView 
            selectedView={selectedView}
            activeTab={activeTab}
            isPinned={isPinned}
            setIsPinned={setIsPinned}
            isFilterOpen={isFilterOpen}
            setIsFilterOpen={setIsFilterOpen}
            isMobile={isMobile}
            selectedLocation={selectedLocation}
            onLocationSelect={handleLocationSelect}
            selectedCrime={selectedCrime}
            setSelectedCrime={setSelectedCrime}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={handleDateRangeChange}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
            selectedData={selectedData}
            setSelectedData={setSelectedData}
            compareMode={compareMode}
            onCompareModeChange={setCompareMode}
            rateViewMode={rateViewMode}
            setRateViewMode={setRateViewMode}
            getActiveCompareMode={getActiveCompareMode}
            mapRange={mapRange}
            onMapRangeChange={handleMapRangeChange}
            hoveredLocation={hoveredLocation}
            setHoveredLocation={setHoveredLocation}
            setAvailableYears={setAvailableYears}
            availableYears={availableYears}
            showCityResolutionModal={showCityResolutionModal}
            setShowCityResolutionModal={setShowCityResolutionModal}
            setCityResolutionData={setCityResolutionData}
            yearCheckInProgress={yearCheckInProgress}
            onSaveSearch={handleSaveSearch}
            userPlan={userPlan}
            mostRecentYear={mostRecentYear}
            closePopupSignal={closePopupSignal}
            searchBlockedRef={searchBlockedRef}
            mapSearchGateRef={mapSearchGateRef}
            exportContext={exportContext}
            exportContextRef={exportContextRef}
            onRequestUpgrade={(reason) => { setUpgradeReason(reason); setShowUpgradeModal(true); }}
          />
        }
      />
      <MobileNavigation 
        selectedView={selectedView} 
        setSelectedView={setSelectedView}
        onOpenSavedSearches={() => setShowSavedSearchesModal(true)}
        onOpenAlerts={() => { setPendingAlertData(null); setAlertsDefaultTab('notifications'); setShowAlertsModal(true); }}
        unreadCount={alertNotifications.filter(n => !n.read).length}
      />
      <Footer />
      <CityResolutionModal
        isOpen={showCityResolutionModal}
        onClose={() => {
          setShowCityResolutionModal(false);
          if (selectedLocation?.info) {
            setSelectedLocation(prev => ({
              ...prev,
              info: {
                ...prev.info,
                fromHeader: false
              }
            }));
          }
        }}
        resolutionData={cityResolutionData}
        selectedDateRange={selectedDateRange}
        availableYears={availableYears}
        onLocationSelect={handleLocationSelect}
        onYearChange={setSelectedDateRange}
      />
      <YearSelectionModal
        isOpen={showYearSelectionModal}
        onClose={() => {
          setShowYearSelectionModal(false);
          setYearModalLocation(null);
          if (selectedLocation?.info) {
            setSelectedLocation(prev => ({
              ...prev,
              info: {
                ...prev.info,
                fromHeader: false
              }
            }));
          }
        }}
        locationInfo={yearModalLocation}
        selectedDateRange={selectedDateRange}
        availableYears={availableYears}
        onYearChange={(year) => {
          dispatch({ type: 'SET_DATE_RANGE', value: year });
          setShowYearSelectionModal(false);
          
          if (yearModalLocation) {
            handleLocationSelect({
              ...yearModalLocation,
              info: {
                ...yearModalLocation.info,
                skipYearCheck: true,
                skipResolutionCheck: true
              }
            });
            setYearModalLocation(null);
          }
        }}
      />
      <SavedSearchesModal
        isOpen={showSavedSearchesModal}
        onClose={() => setShowSavedSearchesModal(false)}
        savedSearches={savedSearches}
        onApply={handleApplySavedSearch}
        onDelete={handleDeleteSavedSearch}
        currentCriteria={{
          locationName: selectedLocation?.info?.name,
          locationType: selectedLocation?.info?.type,
          crime: selectedCrime,
          dateRange: String(selectedDateRange),
          source: selectedSource
        }}
      />
      <AlertsModal
        isOpen={showAlertsModal}
        onClose={() => { setShowAlertsModal(false); setPendingAlertData(null); }}
        alerts={alerts}
        onSave={handleSaveAlert}
        onUpdate={handleUpdateAlert}
        onDelete={handleDeleteAlert}
        pendingAlertData={pendingAlertData}
        defaultTab={alertsDefaultTab}
        notifications={alertNotifications}
        onMarkRead={handleMarkNotificationsRead}
        onMarkAllRead={handleMarkAllNotificationsRead}
      />
      <UsageLimitModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason={upgradeReason}
        plan={userPlan}
        onUpgrade={() => {
          setShowUpgradeModal(false);
          setOpenSubscriptionSignal(prev => prev + 1);
        }}
      />
    </div>
  );
};

export default Krime;